import {
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Spinner
} from 'reactstrap';
import React, { useCallback, useEffect, useMemo } from 'react';
import Form from './Form';
import { useForm } from 'react-hook-form';
import Button from './Button';
import { ICarWash, StatusCarWashLabels } from '../models/CarWash';
import TextField from './TextField';
import DateField from './DateField';
import TagTimeField from './TagTimeField';
import SelectField from './SelectField';
import useUser from '../hooks/useUser';
import Icon from './Icon';
import { useApiGet, useApiPut } from '../hooks/useApi';
import moment from 'moment';
import useCompany from '../hooks/useCompany';
import { useToast } from '../hooks/useToast';
import ICheckList from '../models/CheckList';
import CarWashStatusBadge from './CarWashStatusBadge';
import { UserType } from '../models/User';
import CheckListPhotoGallery from './CheckListPhotoGallery';
import ModalViewCheckList from './ModalViewCheckList';
import ModalAddCheckList from './ModalAddCheckList';

type Props = {
  open: boolean;
  onClose: () => void;
  carWash: ICarWash;
};

const ModalEditTagForm = ({ open, onClose, carWash }: Props) => {
  const { addToast } = useToast();
  const user = useUser();
  const company = useCompany();
  const [checkListMarked, setCheckListMarked] = React.useState<any>({});
  const [
    selectedCheckList,
    setSelectedCheckList
  ] = React.useState<ICheckList | null>(null);
  const [showAddCheckList, setShowAddCheckList] = React.useState(false);
  const [statusOptions, priorityOptions] = useMemo(
    () => [
      Object.entries(StatusCarWashLabels).map(([key, value]) => ({
        value: key,
        label: value
      })),
      company?.items_priority
        ?.filter((x) => x.active)
        .map((x) => ({
          value: x.id!,
          label: x.item!
        })) || []
    ],
    [company]
  );
  const {
    data: checkListResponse,
    isLoading: checkListLoading,
    isError,
    refetch
  } = useApiGet<ICheckList[]>('/check-list', {
    id: carWash?.id.toString(),
    enabled: open,
    refetchOnMount: true
  });

  // Get the most recent checklist (last in the array since backend orders by data_cadastro)
  const checkList = checkListResponse?.[checkListResponse.length - 1];

  const methods = useForm();
  const { mutate, isPending } = useApiPut('/carwash', carWash?.id.toString());
  const { reset } = methods;

  // Refetch checklist data when modal opens to get fresh photos
  useEffect(() => {
    if (open && refetch) {
      refetch();
    }
  }, [open, refetch]);

  useEffect(() => {
    if (open) {
      reset({
        ...carWash,
        status: carWash?.status || statusOptions[0]?.value,
        priority: carWash?.priority || priorityOptions[0]?.value,
        date: moment(carWash?.prom_time).format('YYYY-MM-DD'),
        'notes-check-list': isError ? '' : checkList?.note || ''
      });
      setCheckListMarked(
        isError
          ? {}
          : checkList?.items_details?.reduce(
              (acc, item) => ({ ...acc, [item.id!]: true }),
              {}
            ) || {}
      );
    }
  }, [
    open,
    checkList,
    carWash,
    statusOptions,
    priorityOptions,
    reset,
    isError
  ]);

  const onSubmitHandler = useCallback(
    (formData: any) => {
      const [, promTime] = moment(formData.prom_time).format().split('T');
      const [date] = moment(formData.date).format().split('T');
      // IMPORTANT: Only send fields that are intentionally editable in this modal.
      // Sending fields like `dispatched`, `valet_accepted`, etc. can trigger side-effects
      // in the backend (e.g., clearing valet acceptance when `dispatched` becomes false).
      const data = {
        vehicle: formData.vehicle,
        color: formData.color,
        prom_time: moment(`${date}T${promTime}`)
          .utc()
          .format('YYYY-MM-DDTHH:mm:ssZ'),
        priority: formData.priority,
        status: formData.status,
        delivered: formData.delivered === 'true',
        in_detail: formData.in_detail === 'true',
        location: formData.location,
        note: formData.note,
        company: company?.id
      };
      mutate(data, {
        onSuccess: () => {
          onClose();
          addToast({
            message: carWash ? 'Tag updated successfully' : 'New tag added',
            color: 'success',
            icon: 'check'
          });
        },
        onError: (request: any) => {
          const errors = request?.errors;
          if (errors) {
            Object.keys(errors).forEach((key: any) => {
              methods.setError(key, {
                type: 'manual',
                message: errors[key]
              });
            });
          }
        }
      });
    },
    [company?.id, mutate, onClose, addToast, carWash, methods]
  );

  const checkListItems = useMemo(
    () =>
      company?.items_check_list?.map((item) => ({
        id: item.id,
        label: item.item,
        checked: !!checkListMarked[item.id!]
      })) || [],
    [company, checkListMarked]
  );

  return (
    <>
      <Modal
        isOpen={open}
        toggle={() => onClose()}
        size="xl"
        aria-labelledby="modal-edit-tag-title"
      >
        <Form {...methods} onSubmit={onSubmitHandler}>
          <ModalBody>
            <div className="d-flex justify-content-between align-items-center w-100">
              <span className="fw-bolder h4" id="modal-edit-tag-title">
                #{carWash.tag}
              </span>
              <Button
                color="light"
                className="text-primary"
                onClick={() => onClose()}
                size="lg"
                aria-label="Close modal"
              >
                <Icon local icon="close" width={20} />
              </Button>
            </div>
            <p className="text-secondary">
              {carWash.vehicle}
              <br />
              {carWash.agent_dispatched_detail && (
                <b>
                  {carWash.agent_dispatched_detail?.first_name}{' '}
                  {carWash.agent_dispatched_detail?.last_name}
                </b>
              )}
            </p>
            <div className="d-flex justify-content-between align-items-center w-100">
              <span>Status</span>
              <CarWashStatusBadge status={carWash.status} />
            </div>
            <Row className="mt-4">
              <Col>
                <Row className="mt-4">
                  <Col md={2}>
                    <TextField
                      name="vehicle"
                      label="Vehicle"
                      placeholder="Vehicle"
                      disabled={user?.tipo === UserType.washer}
                    />
                  </Col>
                  <Col md={2}>
                    <TextField
                      name="color"
                      label="Color"
                      placeholder="Color"
                      disabled={
                        user?.tipo === UserType.washer ||
                        user?.tipo === UserType.valet
                      }
                    />
                  </Col>
                  <Col md={3}>
                    <DateField name="date" label="Date" placeholder="Date" />
                  </Col>
                  <Col md={5}>
                    <TagTimeField
                      name="prom_time"
                      label="Prom. Time"
                      placeholder="Prom. Time"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <SelectField
                      name="priority"
                      options={priorityOptions}
                      label="Priority"
                      placeholder="Priority"
                    />
                  </Col>
                  <Col md={4}>
                    <SelectField
                      name="status"
                      options={statusOptions}
                      label="Status"
                      placeholder="Status"
                    />
                  </Col>
                  <Col md={4}>
                    <SelectField
                      name="delivered"
                      options={[
                        {
                          value: 'true',
                          label: 'Yes'
                        },
                        {
                          value: 'false',
                          label: 'No'
                        }
                      ]}
                      label="Delivered"
                      placeholder="Delivered"
                    />
                  </Col>
                </Row>
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="text-secondary fw-bold mb-0">
                      Attached Photos{' '}
                      {checkList && (
                        <span className="text-muted small">
                          (Latest Checklist)
                        </span>
                      )}
                    </p>
                    {checkList && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-decoration-none p-0"
                        onClick={() => setShowAddCheckList(true)}
                      >
                        <Icon icon="plus" local width={14} className="me-1" />
                        <small>Add Checklist</small>
                      </button>
                    )}
                  </div>
                  {checkList ? (
                    <CheckListPhotoGallery
                      pictures={checkList?.pictures_details || []}
                      loading={checkListLoading}
                    />
                  ) : (
                    <div className="text-center p-4 bg-light rounded">
                      <p className="text-muted mb-2">No checklist yet</p>
                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        onClick={() => setShowAddCheckList(true)}
                      >
                        Add First Checklist
                      </Button>
                    </div>
                  )}
                </div>
                <Row>
                  <Col md={6}>
                    <TextField
                      name="location"
                      label="Location"
                      placeholder="Location"
                    />
                  </Col>
                  <Col md={6}>
                    <TextField name="note" label="Notes" placeholder="Notes" />
                  </Col>
                </Row>
              </Col>
              {checkList && (
                <Col sm="3" className="bg-light py-2 rounded">
                  <div>
                    <p className="text-secondary fw-bold">Check-List</p>
                    {checkListLoading ? (
                      <div className="text-center p-3">
                        <Spinner color="primary">Loading...</Spinner>
                      </div>
                    ) : (
                      <div className="w-100 py-2">
                        {checkListItems?.map((item) => (
                          <div
                            key={item.id}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <Label check for={`check-item-${item.id}`}>
                              {item.label}
                            </Label>
                            <FormGroup switch className="d-inline-block">
                              <Input
                                type="switch"
                                role="switch"
                                checked={item.checked}
                                name={`check-item-${item.id}`}
                                id={`check-item-${item.id}`}
                                disabled
                              />
                            </FormGroup>
                          </div>
                        ))}
                        {(!checkListItems || checkListItems.length === 0) && (
                          <p className="small text-secondary">
                            No check-list items
                          </p>
                        )}
                        <br />
                        <Label className="small text-secondary">
                          Note quality control
                        </Label>
                        <Input
                          type="textarea"
                          value={checkList?.note || ''}
                          disabled
                          readOnly
                          rows={3}
                        />
                        {checkList?.user_detail && (
                          <div className="mt-3 pt-2 border-top">
                            <p className="small text-secondary mb-1">
                              <strong>Checklist completed by:</strong>
                            </p>
                            <p className="small mb-1">
                              {checkList.user_detail.first_name}{' '}
                              {checkList.user_detail.last_name}
                            </p>
                            <p className="small text-secondary mb-0">
                              {moment(checkList.data_cadastro).format(
                                'MMM DD, YYYY [at] h:mm A'
                              )}
                            </p>
                          </div>
                        )}

                        {checkListResponse && checkListResponse.length > 1 && (
                          <div className="mt-3 pt-2 border-top">
                            <p className="small text-secondary mb-2">
                              <strong>Previous Checklists:</strong>
                            </p>
                            <div className="d-flex flex-column gap-2">
                              {checkListResponse
                                .slice(0, -1)
                                .reverse()
                                .map((item, index) => (
                                  <button
                                    key={item.id}
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary text-start d-flex justify-content-between align-items-center"
                                    onClick={() => setSelectedCheckList(item)}
                                  >
                                    <div>
                                      <div className="small">
                                        {item.user_detail
                                          ? `${item.user_detail.first_name} ${item.user_detail.last_name}`
                                          : 'Unknown'}
                                      </div>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: '0.75rem' }}
                                      >
                                        {moment(item.data_cadastro).format(
                                          'MMM DD, YYYY [at] h:mm A'
                                        )}
                                      </div>
                                    </div>
                                    <Icon icon="arrow-right" local width={16} />
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Col>
              )}
            </Row>
          </ModalBody>
          <ModalFooter className="border-0">
            <div className="d-flex justify-content-between align-items-center w-100">
              <Button
                outline
                color="danger"
                onClick={onClose}
                type="button"
                size="lg"
                loading={isPending}
                aria-label="Cancel and close"
              >
                Cancel
              </Button>
              <Button
                size="lg"
                color="primary"
                loading={isPending}
                aria-label="Save changes"
              >
                Save
              </Button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>
      {selectedCheckList && (
        <ModalViewCheckList
          open={!!selectedCheckList}
          onClose={() => setSelectedCheckList(null)}
          checkList={selectedCheckList}
        />
      )}
      <ModalAddCheckList
        open={showAddCheckList}
        onClose={() => setShowAddCheckList(false)}
        carWashId={carWash.id}
        onSuccess={() => refetch()}
      />
    </>
  );
};
export default ModalEditTagForm;
